import { useCallback } from 'react';
import { ACM } from '../../../../constants/UserRoles';

export const useGoalStatistics = (studentGoalsData, typeOfUser) => {
  const processStudentGoalsData = useCallback(() => {
    if (!studentGoalsData || studentGoalsData.length === 0) return;

    const goalWiseData = {};

    const getLast7Days = () => {
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        days.push(date.toLocaleDateString('en-CA'));
      }
      return days;
    };

    if (ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isTeacher) {
      studentGoalsData.forEach((student) => {
        const studentId = student.id;
        if (Array.isArray(student?.dataCollection)) {
          student.dataCollection.forEach((collection) => {
            const goalTitle = collection?.info?.GoalDetails?.GoalTitle;
            const goalType =
              collection?.info?.DataCollection?.measurementType?.Target;
            const goalNumber = collection?.info?.GoalDetails?.GoalNumber;
            const targetDuration = Number.parseFloat(
              collection?.info?.DataCollection?.measurementType
                ?.TargetDuration || 0
            );

            if (!goalTitle || targetDuration <= 0) return;

            const goalKey = `${studentId}-${goalTitle}`;

            if (!goalWiseData[goalKey]) {
              goalWiseData[goalKey] = {
                studentId,
                goalTitle,
                targetDuration,
                goalNumber,
                goalType,
                dates: {},
              };
            }

            if (Array.isArray(collection?.goalData)) {
              collection.goalData.forEach((dataEntry) => {
                const createdAtDate = new Date(
                  (dataEntry.createdAt?._seconds ||
                    dataEntry.createdAt?.seconds) * 1000
                ).toLocaleDateString('en-CA');

                const dataPoints = dataEntry?.info?.data_points || [];

                if (!goalWiseData[goalKey].dates[createdAtDate]) {
                  goalWiseData[goalKey].dates[createdAtDate] = [];
                }

                dataPoints.forEach((point) => {
                  const goalValue = Number.parseFloat(point.goal_value || 0);
                  goalWiseData[goalKey].dates[createdAtDate].push(goalValue);
                });
              });
            }
          });
        }
      });
    } else {
      studentGoalsData.forEach((student) => {
        const studentId = student?.studentData?.StudentID;
        const goalTitle = student?.studentData?.GoalDetails?.GoalTitle;
        const goalType =
          student?.studentData?.DataCollection?.measurementType?.Target;
        const goalNumber = student?.studentData?.GoalDetails?.GoalNumber;
        const targetDuration = Number.parseFloat(
          student?.studentData?.DataCollection?.measurementType
            ?.TargetDuration || 0
        );

        if (!goalTitle || targetDuration <= 0) return;

        const goalKey = `${studentId}-${goalTitle}`;

        if (!goalWiseData[goalKey]) {
          goalWiseData[goalKey] = {
            studentId,
            goalTitle,
            targetDuration,
            goalType,
            goalNumber,
            dates: {},
          };
        }

        if (Array.isArray(student?.studentData?.goalData)) {
          student?.studentData?.goalData.forEach((dataEntry) => {
            const createdAtDate = new Date(
              (dataEntry.createdAt?._seconds || dataEntry.createdAt?.seconds) *
                1000
            ).toLocaleDateString('en-CA');

            const dataPoints = dataEntry?.info?.data_points || [];

            if (!goalWiseData[goalKey].dates[createdAtDate]) {
              goalWiseData[goalKey].dates[createdAtDate] = [];
            }

            dataPoints.forEach((point) => {
              const goalValue = Number.parseFloat(point.goal_value || 0);
              goalWiseData[goalKey].dates[createdAtDate].push(goalValue);
            });
          });
        }
      });
    }

    const allDatesSet = new Set();
    Object.values(goalWiseData).forEach(({ dates }) => {
      Object.keys(dates).forEach((date) => allDatesSet.add(date));
    });

    const allDates = Array.from(allDatesSet).sort();

    const last7Days = getLast7Days();
    const combinedDates = Array.from(
      new Set([...allDates, ...last7Days])
    ).sort();

    const formattedData = Object.entries(goalWiseData).map(
      ([
        goalKey,
        { studentId, goalTitle, goalNumber, targetDuration, goalType, dates },
      ]) => {
        const graphData = combinedDates.map((date) => {
          const goalValues = dates[date] || [];
          let percentage = 0;

          if (goalValues.length > 1) {
            const totalGoalValue = goalValues.reduce(
              (sum, val) => sum + val,
              0
            );
            const avgGoalValue = totalGoalValue / goalValues.length;
            percentage =
              goalType === 'Atleast'
                ? (avgGoalValue / targetDuration) * 100
                : (1 - (avgGoalValue - targetDuration) / avgGoalValue) * 100;
          } else if (goalValues.length === 1) {
            percentage =
              goalType === 'Atleast'
                ? (goalValues[0] / targetDuration) * 100
                : (1 - (goalValues[0] - targetDuration) / goalValues[0]) * 100;
          }

          return { date, percentage: Number(percentage.toFixed(2)) };
        });

        return { studentId, goalTitle, graphData, goalNumber };
      }
    );

    return formattedData;
  }, [studentGoalsData, typeOfUser]);

  return { processStudentGoalsData };
};